import * as React from "react";
import {
  Box,
  Card,
  createStyles,
  makeStyles,
  Typography,
} from "@material-ui/core";
import { ClassNameMap } from "@material-ui/core/styles/withStyles";
import { filter, keys, last, mapKeys, orderBy, reverse, toPairs } from "lodash";
import {
  Bar,
  BarChart,
  Legend,
  LegendPayload,
  LegendProps,
  ResponsiveContainer,
  Tooltip,
  TooltipPayload,
  TooltipProps,
  XAxis,
  YAxis,
} from "recharts";
import { titleize } from "underscore.string";

import { Button, CustomizedXAxisTick } from "components";
import { formatBigNumber, formatMoney } from "lib/helpers";
import { earningSourceNameToDisplayName } from "services/valuation/constants";
import { TopEarningsReport } from "services/valuation/types";
import theme from "theme";
import { REGULAR_CHART_COLOR_LIST } from "theme/constants";

const useStyles = makeStyles(() =>
  createStyles({
    title: {
      fontSize: "18px",
      fontWeight: theme.typography.fontWeightBold,
    },
    subtitle: {
      fontSize: "14px",
    },
    customEarningsByPeriodChartWrapper: {
      width: "100%",
      height: "calc(100% - 56px)",
      maxHeight: "520px",
      padding: "0px 24px 0px 0px",
    },
    tooltipContainer: {
      padding: theme.spacing(2),
      border: `1px solid ${theme.custom.palette.gray.dark}`,
    },
    legendItem: {
      width: "100px",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
  }),
);
interface CustomTooltipProps extends TooltipProps {
  selectedBar: number;
  classes: ClassNameMap;
}
const CustomTooltip: React.FC<CustomTooltipProps> = ({
  active,
  payload,
  label,
  selectedBar,
  classes,
}) => {
  if ((active || !!selectedBar) && payload && payload.length) {
    return (
      <Card className={classes.tooltipContainer}>
        <p className="label">{label}</p>
        {payload.map((item: TooltipPayload, index: number) => (
          <p
            key={index}
            style={{
              fontWeight:
                selectedBar === index
                  ? theme.typography.fontWeightBold
                  : "normal",
            }}
          >
            &nbsp;&nbsp;
            {item.name}: ${formatMoney(String(item.value), 0)}
          </p>
        ))}
      </Card>
    );
  }

  return null;
};

const CustomLegend: React.FC<LegendProps> = (props: LegendProps) => {
  const { payload, onClick } = props;
  const legendItemWidthPercent =
    payload.length > 3
      ? 100 / Math.ceil(payload.length / 2)
      : payload.length >= 2
      ? 50
      : 100;
  return (
    <ul style={{ textAlign: "center", margin: 0, padding: 0 }}>
      {payload.map((entry: LegendPayload, index: number) => (
        <Button
          key={`item-${index}`}
          style={{
            padding: "0px 10px 3px 10px",
            maxWidth: `${legendItemWidthPercent}%`,
            textAlign: legendItemWidthPercent === 100 ? "center" : "start",
          }}
          onClick={() => onClick(index)}
        >
          <li
            style={{
              display: "inline-block",
              width: "100%",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              padding: "0px 0px",
              cursor: "pointer",
            }}
          >
            <svg
              className="recharts-surface"
              width="20"
              height="20"
              style={{
                display: "inline-block",
                verticalAlign: "middle",
                marginRight: "4px",
              }}
              viewBox="0 0 32 32"
              version="1.1"
            >
              <path
                stroke="none"
                fill={entry.color}
                d="M0,4h32v24h-32z"
                className="recharts-legend-icon"
              ></path>
            </svg>
            <span
              style={{
                color: theme.custom.palette.black.main,
                fontSize: ".85em",
              }}
            >
              {entry.value}
            </span>
          </li>
        </Button>
      ))}
    </ul>
  );
};

interface TopEarningsByYearProps {
  title: string;
  data: TopEarningsReport[];
  titleizeKey?: boolean;
}

const TopEarningsByYear: React.FC<TopEarningsByYearProps> = React.memo(
  ({ title, data, titleizeKey = true }) => {
    const classes = useStyles();
    const restLabel = "Other";
    const formattedData = data.map((item: TopEarningsReport, index) => {
      return {
        ...mapKeys(item, (_value, key) =>
          earningSourceNameToDisplayName[key] || titleizeKey
            ? titleize(key)
            : key,
        ),
        ltmAxis: index === data.length - 1 ? "(LTM)" : "",
        Year: titleize(String(item.year)).replace("_", " "),
      };
    });
    const orderedBars = reverse(
      orderBy(
        filter(
          toPairs(last(formattedData)),
          pair => ![restLabel, "Year", "year", "ltmAxis"].includes(pair[0]),
        ),
        1,
      ),
    ).map(pair => pair[0]);
    if (keys(last(formattedData)).includes(restLabel)) {
      orderedBars.push(restLabel);
    }
    const [selectedBar, setSelectedBar] = React.useState(undefined);
    const handleClick = (index: number) => {
      if (selectedBar === index) {
        setSelectedBar(undefined);
      } else {
        setSelectedBar(index);
      }
    };

    return (
      <>
        <Box className={classes.customEarningsByPeriodChartWrapper}>
          <Box mb={2}>
            <Typography className={classes.title}>{title}</Typography>
            <Typography className={classes.subtitle}>
              Last {formattedData.length} Years Earnings
            </Typography>
          </Box>
          <ResponsiveContainer>
            <BarChart
              data={formattedData}
              margin={{
                top: 0,
                right: 0,
                left: 0,
                bottom: 0,
              }}
            >
              <Tooltip
                content={
                  <CustomTooltip selectedBar={selectedBar} classes={classes} />
                }
                formatter={(value, name) => [
                  `$${formatMoney(value as number, 0)}`,
                  name,
                ]}
                itemStyle={{ color: theme.custom.palette.black.main }}
                animationEasing="ease-in-out"
              />
              <Legend
                content={<CustomLegend onClick={handleClick} />}
                wrapperStyle={{ bottom: 20 }}
              />
              {orderedBars.map((key, index) => {
                return (
                  <Bar
                    key={key}
                    dataKey={key}
                    stackId="a"
                    onClick={() => handleClick(index)}
                    maxBarSize={100}
                    fill={
                      selectedBar === undefined || selectedBar === index
                        ? REGULAR_CHART_COLOR_LIST[index]
                        : theme.custom.palette.gray.main
                    }
                    isAnimationActive={false}
                    style={{ cursor: "pointer" }}
                  />
                );
              })}
              <XAxis
                stroke={theme.custom.palette.black.main}
                xAxisId={0}
                tickLine={false}
                padding={{ left: 0, right: 0 }}
                dataKey="Year"
                tickFormatter={value => titleize(value.replace("_", " "))}
              />
              <XAxis
                dataKey="ltmAxis"
                xAxisId={1}
                stroke="1"
                padding={{ left: 0, right: 0 }}
                tick={<CustomizedXAxisTick />}
              />
              <YAxis
                tickLine={false}
                stroke={theme.custom.palette.black.main}
                tickFormatter={value => `$${formatBigNumber(value)}`}
                domain={[0, "auto"]}
                scale="linear"
              />
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </>
    );
  },
);

export default TopEarningsByYear;
